// src/pages/Home.js

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLock,
  faChalkboardTeacher,
  faCameraRetro,
  faBolt,
  faGlobe,
  faUserCog,
  faShareAlt,
  faUsers,
  faUserShield,
  faMobileAlt,
  faUserTie,
} from "@fortawesome/free-solid-svg-icons";
import "../styles/Home.css";

const Home = () => {
  const [scrollY, setScrollY] = useState(0);

  // Parallax scroll
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY || window.pageYOffset || 0;
      setScrollY(y);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Scroll-reveal animations
  useEffect(() => {
    const elements = document.querySelectorAll(".reveal-on-scroll");
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            obs.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.18,
        rootMargin: "0px 0px -80px 0px",
      }
    );

    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="home-container" style={{ "--scrollY": `${scrollY}px` }}>
      {/* SEO Metadata */}
      <Helmet>
        <title>GhrianGhraffy - Secure Photo Sharing for Irish Primary Schools</title>
        <meta
          name="description"
          content="A secure photo-sharing platform for Irish Primary schools to capture, approve and share photos with parents and school communities."
        />
        <meta
          name="keywords"
          content="irish primary school, school photo sharing, teachers photo app, school photography management, secure school photos"
        />
      </Helmet>

      <main>
        {/* Hero Section */}
        <header className="hero">
          <div className="hero-inner">
            <div className="hero-copy">
              <div className="hero-pill-row">
                <span className="hero-pill">Secure by design</span>
                <span className="hero-pill hero-pill-outline">
                  Built for Ireland&apos;s Primary schools
                </span>
              </div>

              <h1>Secure Photo Sharing for Ireland&apos;s Primary Schools.</h1>
              <p className="tagline">
                Capture Classroom Moments and Share Them Safely with Parents, School Website
                and Social Media in Just a Few Taps.
              </p>

              <div className="cta-buttons">
                <Link to="/principal-registration" className="cta-button primary">
                  Get Started with Free Trial
                </Link>
                <Link to="/learn-more" className="cta-button secondary">
                  See How it Works
                </Link>
              </div>

              <div className="hero-meta">
                <div className="meta-item">
                  <FontAwesomeIcon icon={faLock} />
                  <span>Privacy-first, Built for GDPR-conscious Schools</span>
                </div>
                <div className="meta-item">
                  <FontAwesomeIcon icon={faBolt} />
                  <span>Workload Reduction</span>
                </div>
              </div>
            </div>

            <div className="hero-visual">
              <div className="device-shell">
                <div className="device-header">
                  <span className="device-dot" />
                  <span className="device-dot" />
                  <span className="device-dot" />
                </div>
                <div className="device-screen">
                  <div className="device-photo-card">
                    <div className="device-photo" />
                    <div className="device-photo-meta">
                      <span className="device-photo-tag">#FirstClass</span>
                      <span className="device-photo-status">Awaiting approval</span>
                    </div>
                  </div>

                  <div className="device-photo-card secondary">
                    <div className="device-photo small" />
                    <div className="device-photo-meta">
                      <span className="device-photo-tag">#SportsDay</span>
                      <span className="device-photo-status success">
                        Approved • sent to parents
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="floating-note floating-note-top">
                <FontAwesomeIcon icon={faCameraRetro} />
                <span>New photos from Ms. O&apos;Brien</span>
              </div>

              <div className="floating-note floating-note-bottom">
                <FontAwesomeIcon icon={faShareAlt} />
                <span>Published to school website</span>
              </div>
            </div>
          </div>
        </header>

        {/* Strip / value statement */}
        <section className="section strip">
          <p>
            GhrianGhraffy brings Principals, Staff and Parents together around the Moments that
            Matter — without compromising privacy or adding to anyone&apos;s workload.
          </p>
        </section>

        {/* Feature Grid */}
        <section className="section" id="features">
          <div className="section-inner">
            <div className="section-header">
              <p className="eyebrow">Why GhrianGhraffy</p>
              <h2>Everything you Need in One Simple Workflow</h2>
              <p className="section-lead">
                Replace scattered WhatsApp groups, USB keys and untracked Photo folders with a
                Single, Secure System built specifically for Irish Primary Schools.
              </p>
            </div>

            <div className="feature-grid">
              <article className="feature-card reveal-on-scroll">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faUserShield} />
                </div>
                <h3>Secure by default</h3>
                <p>
                  Photos can stay within your school environment and are visible only to
                  authorised principals, staff and parents.
                </p>
              </article>

              <article className="feature-card reveal-on-scroll">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faMobileAlt} />
                </div>
                <h3>Built for Busy Teachers</h3>
                <p>
                  Teachers Snap and Send in seconds — No camera roll clutter, no manual
                  organising, just point, click and done.
                </p>
              </article>

              <article className="feature-card reveal-on-scroll">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faShareAlt} />
                </div>
                <h3>Multi-channel sharing</h3>
                <p>
                  Send approved photos to GhrianGhraffy.com, your School Website and Instagram
                  simultaneously from one Secure App.
                </p>
              </article>

              <article className="feature-card reveal-on-scroll">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faUserCog} />
                </div>
                <h3>Principal-friendly controls</h3>
                <p>
                  Approve Photos in One Click, manage staff access, and keep an overview of
                  what&apos;s being shared and where.
                </p>
              </article>

              <article className="feature-card reveal-on-scroll">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faChalkboardTeacher} />
                </div>
                <h3>Delight parents</h3>
                <p>
                  Parents enjoy a simple, password-protected view of their child&apos;s class
                  photos — with no extra apps to juggle.
                </p>
              </article>

              <article className="feature-card reveal-on-scroll">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faGlobe} />
                </div>
                <h3>Works with your website</h3>
                <p>
                  A lightweight plugin makes it effortless to display galleries on your
                  existing WordPress school website.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="section muted" id="how-it-works">
          <div className="section-inner">
            <div className="section-header">
              <p className="eyebrow">How it Works</p>
              <h2>From Classroom to Web in 3 steps</h2>
            </div>

            <div className="steps-grid">
              <div className="step-card reveal-on-scroll">
                <div className="step-number">1</div>
                <h3>Capture</h3>
                <p>
                  Teachers use the GhrianGhraffy app to take photos during class, sports days
                  or school events — without saving to their personal device.
                </p>
              </div>

              <div className="step-card reveal-on-scroll">
                <div className="step-number">2</div>
                <h3>Approve</h3>
                <p>
                  Principals (or nominated staff) review and approve photos in a clean, focused
                  dashboard. One click, and they&apos;re ready to share.
                </p>
              </div>

              <div className="step-card reveal-on-scroll">
                <div className="step-number">3</div>
                <h3>Share</h3>
                <p>
                  Approved photos are automatically shared with parents and, if you choose,
                  your school website and Instagram — all from one place.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Role-based value props */}
        <section className="section" id="for-schools">
          <div className="section-inner">
            <div className="section-header">
              <p className="eyebrow">Made for everyone in your school</p>
              <h2>Different dashboards, same simple experience</h2>
            </div>

            <div className="audience-grid">
              <article className="audience-card reveal-on-scroll">
                <div className="audience-icon">
                  <FontAwesomeIcon icon={faUserTie} />
                </div>
                <h3>For Principals</h3>
                <p>
                  Stay in control of what&apos;s shared, keep data safe and show off school life
                  — without adding another admin job to your day.
                </p>
              </article>

              <article className="audience-card reveal-on-scroll">
                <div className="audience-icon">
                  <FontAwesomeIcon icon={faChalkboardTeacher} />
                </div>
                <h3>For Staff</h3>
                <p>
                  Capture and upload photos in the moment. No resizing, renaming or chasing
                  permissions across multiple channels.
                </p>
              </article>

              <article className="audience-card reveal-on-scroll">
                <div className="audience-icon">
                  <FontAwesomeIcon icon={faUsers} />
                </div>
                <h3>For Parents</h3>
                <p>
                  Enjoy a secure, ad-free view of your child&apos;s school memories from any
                  device, any time.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="section muted" id="testimonials">
          <div className="section-inner">
            <div className="section-header">
              <p className="eyebrow">What schools are saying</p>
              <h2>Loved by principals &amp; teachers</h2>
            </div>

            <div className="testimonial-grid">
              <article className="testimonial-card reveal-on-scroll">
                <p>
                  GhrianGhraffy has transformed how we share school life with families. It&apos;s
                  now part of our daily routine.
                </p>
                <h4>— Mrs. Browne, Principal</h4>
              </article>

              <article className="testimonial-card reveal-on-scroll">
                <p>
                  I can share photos from class in just a few taps. Parents feel more connected,
                  and it takes me less time than before.
                </p>
                <h4>— Ms. O&apos;Brien, Teacher</h4>
              </article>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="section final-cta">
          <div className="section-inner">
            <div className="final-cta-card reveal-on-scroll">
              <h2>Ready to see GhrianGhraffy in action?</h2>
              <p>
                Start a secure photo-sharing space for your school in just a few minutes.
              </p>
              <div className="cta-buttons">
                <Link to="/principal-registration" className="cta-button primary">
                  Get started with Free Trial
                </Link>
                <a
                  href="mailto:information@ghrianghraffy.com"
                  className="cta-button tertiary"
                >
                  Talk to us
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-inner">
          <p>&copy; {new Date().getFullYear()} GhrianGhraffy. All rights reserved.</p>
          <div className="footer-links">
            <Link to="/terms">Terms &amp; Conditions</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/contact">Contact Us</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;